import { SetStateAction, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import dayjs, { Dayjs } from "dayjs";
import classNames from "classnames";
import { CheckboxSelectAll } from "@/components/common/checkbox/CheckboxSelectAll";
import { Checkbox } from "@/components/common/checkbox/Checkbox";
import { Invoice, InvoiceDetails } from "@/interfaces/invoice";
import { DownloadIcon } from "@/public/icons/DownloadIcon";
import { getCustomerDocumentFile } from "@/components/pages/billings/_helpers/getDocumentFile";
import { Loader } from "@/components/common/loader/Loader";
import { getPrestationTypeFromLabel } from "./mappingPrestation";
import styles from "./Invoices.module.scss";

export type InvoicesProps = {
  invoices: Invoice[];
  startDate: Dayjs;
  endDate: Dayjs;
  itemsChecked: string[];
  setItemsChecked: React.Dispatch<SetStateAction<string[]>>;
};

export const Invoices = ({
  invoices,
  startDate,
  endDate,
  itemsChecked,
  setItemsChecked,
}: InvoicesProps) => {
  const intl = useIntl();

  const [downloading, setDownloading] = useState<string>();
  const [invoiceList, setInvoiceList] = useState<Invoice[]>([]);
  const [checkableList, setCheckableList] = useState<string[]>([]);

  const select = (id: string): void => {
    if (!itemsChecked.includes(id)) {
      setItemsChecked([...itemsChecked, id]);
    } else {
      setItemsChecked(itemsChecked.filter((i) => i !== id));
    }
  };

  const selectAll = (): void => {
    let doUnselect = true;
    const newSelection: string[] = itemsChecked.filter(
      (id) => !checkableList.includes(id)
    );

    checkableList.map((id) => {
      doUnselect = doUnselect && itemsChecked.includes(id);
    });

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (doUnselect) {
      setItemsChecked(newSelection);
    } else {
      setItemsChecked(newSelection.concat(checkableList));
    }
  };

  const getPrestationType = (type: string, details: InvoiceDetails[]) => {
    if (type === "Location") {
      return intl.formatMessage({ id: "locationType" });
    } else if (type === "Additional" || type === "Miscellaneous") {
      return getPrestationTypeFromLabel(details[0]?.optionLabel) || "";
    } else if (type === "Intermediate") {
      return intl.formatMessage({ id: "intermediateType" });
    }
  };

  const downloadFile = (invoice: Invoice) => {
    if (downloading === undefined) {
      setDownloading(invoice.id);
      getCustomerDocumentFile({ invoice: invoice })
        .then((response: Blob) => {
          const objectUrl = window.URL.createObjectURL(response);
          window.open(objectUrl, "_blank", "noopener,noreferrer");
          window.URL.revokeObjectURL(objectUrl);
          setDownloading(undefined);
        })
        .catch(() => setDownloading(undefined));
    }
  };

  useEffect(() => {
    const monthlyInvoices = invoices
      .filter(
        (invoice) =>
          dayjs(invoice.invoiceDate).isAfter(startDate) &&
          dayjs(invoice.invoiceDate).isBefore(endDate)
      )
      .sort((a, b) => {
        if (a.invoiceDate > b.invoiceDate) {
          return 1;
        } else if (a.invoiceDate < b.invoiceDate) {
          return -1;
        } else {
          return 0;
        }
      });

    setInvoiceList(monthlyInvoices);

    const checks: string[] = [];
    monthlyInvoices.map((invoice) => {
      if (!invoice.isBalanced && invoice.balance > 0) {
        checks.push(invoice.id);
      }
    });

    setCheckableList(checks);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices, startDate, endDate]);

  return (
    <>
      <div className={styles.invoicesDesktop}>
        <table>
          <thead>
            <tr>
              <th className={styles.select}>
                {checkableList.length > 0 && (
                  <CheckboxSelectAll
                    itemsChecked={itemsChecked.filter((item) =>
                      invoices.find((i) => i.id === item)
                    )}
                    setChecked={selectAll}
                    maxLength={checkableList.length}
                  />
                )}
              </th>
              <th className={styles.id}>
                {intl.formatMessage({ id: "billingNumber" })}
              </th>
              <th className={styles.date}>
                {intl.formatMessage({ id: "date" })}
              </th>
              <th className={styles.type}>
                {intl.formatMessage({ id: "prestation" })}
              </th>
              <th className={styles.isPaid}>
                {intl.formatMessage({ id: "status" })}
              </th>
              <th className={styles.price}>
                {intl.formatMessage({ id: "amount" })}
              </th>
              <th className={styles.pdf}></th>
            </tr>
          </thead>
          <tbody>
            {invoiceList.map((invoice, index) => {
              const isPaid: boolean =
                invoice.isBalanced || invoice.balance <= 0;

              return (
                <tr className={styles.invoice} key={`contract-${index}`}>
                  <td className={styles.select}>
                    {!isPaid && (
                      <Checkbox
                        id={invoice.id}
                        itemsChecked={itemsChecked}
                        setChecked={() => select(invoice.id)}
                      />
                    )}
                  </td>
                  <td className={styles.id}>{invoice.id}</td>
                  <td className={styles.date}>
                    {dayjs(invoice.invoiceDate).format("DD/MM/YYYY")}
                  </td>
                  <td className={styles.type}>
                    {getPrestationType(invoice.type, invoice.details)}
                  </td>
                  <td className={styles.isPaid}>
                    <div
                      className={classNames(styles.status, {
                        [styles.unpaid]: !isPaid,
                      })}
                    >
                      {intl.formatMessage({
                        id: !isPaid
                          ? "unpaid"
                          : invoice.kind === "Invoice"
                          ? "paid"
                          : "credit",
                      })}
                    </div>
                  </td>
                  <td
                    className={classNames(styles.price, {
                      [styles.unpaid]: !isPaid,
                    })}
                  >
                    {isPaid
                      ? invoice.taxeIncludedPrice
                          .toFixed(2)
                          .replace(/[.,]00$/, "")
                      : invoice.balance.toFixed(2).replace(/[.,]00$/, "")}{" "}
                    €
                  </td>
                  <td className={styles.pdf}>
                    <div
                      className={styles.download}
                      onClick={() => downloadFile(invoice)}
                    >
                      {downloading !== invoice.id && <DownloadIcon />}
                      {downloading === invoice.id && (
                        <Loader className={styles.loader} />
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className={styles.invoicesMobile}>
        <div className={styles.selectAll}>
          <CheckboxSelectAll
            itemsChecked={itemsChecked}
            setChecked={selectAll}
            maxLength={checkableList.length}
          />
          <span className={styles.label}>
            {intl.formatMessage({ id: "selectAll" })}
          </span>
        </div>
        {invoiceList.map((invoice, index) => (
          <div className={styles.invoice} key={`contract-${index}`}>
            <div className={styles.id}>
              <div className={styles.label}>
                {!invoice.isBalanced && (
                  <Checkbox
                    id={invoice.id}
                    itemsChecked={itemsChecked}
                    setChecked={() => select(invoice.id)}
                  />
                )}
              </div>
              <div className={styles.value}>
                {invoice.id}
                <div className={styles.pdf}>
                  <div
                    className={styles.download}
                    onClick={() => downloadFile(invoice)}
                  >
                    {downloading !== invoice.id && <DownloadIcon />}
                    {downloading === invoice.id && (
                      <Loader className={styles.loader} />
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.date}>
              <div className={styles.label}>
                {intl.formatMessage({ id: "date" })}
              </div>
              <div className={styles.value}>
                {dayjs(invoice.invoiceDate).format("DD/MM/YYYY")}
              </div>
            </div>
            <div className={styles.type}>
              <div className={styles.label}>
                {intl.formatMessage({ id: "prestation" })}
              </div>
              <div className={styles.value}>
                {getPrestationType(invoice.type, invoice.details)}
              </div>
            </div>
            <div className={styles.status}>
              <div className={styles.label}>
                {intl.formatMessage({ id: "status" })}
              </div>
              <div
                className={classNames(styles.value, {
                  [styles.unpaid]: !invoice.isBalanced,
                })}
              >
                {intl.formatMessage({
                  id: !invoice.isBalanced ? "unpaid" : "paid",
                })}
              </div>
            </div>
            <div className={styles.prix}>
              <div className={styles.label}>
                {intl.formatMessage({ id: "price" })}
              </div>
              <div
                className={classNames(styles.value, {
                  [styles.unpaid]: !invoice.isBalanced,
                })}
              >
                {invoice.taxeIncludedPrice} €
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};
